import client from '../client';
import type { ApiResponse } from '../../types/api';
import type { VerificationProgress } from '../../types/auth';

export type VerificationCountry =
  | 'saudi' | 'uae' | 'egypt' | 'jordan' | 'lebanon' | 'kuwait' | 'qatar'
  | 'bahrain' | 'oman' | 'morocco' | 'algeria' | 'tunisia' | 'iraq' | 'syria'
  | 'yemen' | 'palestine' | 'sudan' | 'libya' | 'other';

// 'discord' is the relaxed Saudi set: national ID only, plus a photo of it held
// beside the merchant's Discord handle. Offered only to Discord-signup accounts
// (the backend re-checks, so this type is a UI convenience, not a gate).
export type SaudiDocumentType = 'freelance' | 'commercial' | 'discord';

export interface VerificationOptions {
  discord: {
    connected: boolean;
    canUseDiscordPath: boolean;
    source: 'signup' | 'referrer' | null;
    username: string;
  };
  /** The exact handle the merchant must write on the paper. */
  proofLabel: string;
  saudiDocumentTypes: SaudiDocumentType[];
  defaultSaudiDocumentType: SaudiDocumentType | null;
  usernameProofRequiredFor: { nonSaudi: boolean; saudiDocumentTypes: SaudiDocumentType[] };
}

export interface VerifyIdentityPayload {
  name: string;
  country: VerificationCountry;
  documentType?: SaudiDocumentType;
  documentFile: string;
  documentNumber?: string;
  nationalIdFile?: string;
  nationalIdNumber?: string;
  /** Photo of the document next to the username on paper or a screen. */
  usernameProofFile?: string;
  /** Optional second photo: the merchant holding their document. Kept separate
   *  so no one has to fit document + username + face into one frame. */
  faceProofFile?: string;
}

export const verificationApi = {
  /** Which document sets this merchant may use, and what to write on the paper. */
  options: async (): Promise<VerificationOptions> => {
    const res = await client.get<ApiResponse<VerificationOptions>>('/dashboard/auth/verification-options');
    return res.data.data!;
  },

  submit: async (payload: VerifyIdentityPayload): Promise<{ verificationProgress: VerificationProgress }> => {
    const res = await client.post<ApiResponse<{ verificationProgress: VerificationProgress }>>(
      '/dashboard/auth/verify-identity',
      payload,
    );
    return res.data.data!;
  },
};
